/**
 * Caption files from the aligned words: SRT, VTT and a plain transcript, all
 * built from one cue list. Browser-safe, pure.
 *
 * Cues hold at most 2 lines of 42 characters and last at most 6 s. They break
 * at sentence ends (once a cue has some length) and at pauses, and prefer a
 * clause break over a mid-phrase break when a cue is full.
 */

import { endsSentence } from "./align";
import type { TimedWord } from "./types";

export interface CaptionCue {
	index: number;
	start: number;
	end: number;
	text: string;
	lines: string[];
}

/** Balanced wrap into at most two lines of `max` chars; null if it can't fit. */
export function wrapCaptionLines(text: string, max = 42): string[] | null {
	if (text.length <= max) return [text];
	const words = text.split(" ");
	let best: { score: number; lines: string[] } | null = null;
	for (let i = 1; i < words.length; i++) {
		const a = words.slice(0, i).join(" ");
		const b = words.slice(i).join(" ");
		if (a.length <= max && b.length <= max) {
			const score = Math.max(a.length, b.length);
			if (!best || score < best.score) best = { score, lines: [a, b] };
		}
	}
	return best?.lines ?? null;
}

interface CueWord {
	w: string;
	start: number;
	end: number;
}

/** Pack timed words into cues. */
export function packCaptionCues(
	words: readonly TimedWord[],
	options: {
		maxChars?: number;
		maxDurSec?: number;
		gapBreakSec?: number;
		minDurSec?: number;
	} = {},
): CaptionCue[] {
	const maxChars = options.maxChars ?? 42;
	const maxDur = options.maxDurSec ?? 6;
	const gapBreak = options.gapBreakSec ?? 1;
	const minDur = options.minDurSec ?? 0.8;
	const raw: Array<{ start: number; end: number; text: string }> = [];
	let cur: CueWord[] = [];
	const text = (ws: CueWord[]) => ws.map((w) => w.w).join(" ");
	const fits = (ws: CueWord[]) =>
		!!wrapCaptionLines(text(ws), maxChars) &&
		(ws[ws.length - 1]?.end ?? 0) - (ws[0]?.start ?? 0) <= maxDur;
	const emit = (k: number) => {
		const part = cur.slice(0, k);
		cur = cur.slice(k);
		if (part.length) {
			raw.push({
				start: part[0]?.start ?? 0,
				end: part[part.length - 1]?.end ?? 0,
				text: text(part),
			});
		}
	};
	for (const [w, start, end] of words) {
		const word = { w, start, end };
		if (cur.length) {
			const gap = word.start - (cur[cur.length - 1]?.end ?? 0) > gapBreak;
			if (gap) emit(cur.length);
			else if (!fits([...cur, word])) {
				let j = -1;
				for (
					let x = cur.length - 2;
					x >= Math.floor(cur.length * 0.4);
					x--
				) {
					if (/[,;:]["”')]*$/.test(cur[x]?.w ?? "")) {
						j = x;
						break;
					}
				}
				if (j >= 0) {
					emit(j + 1);
					if (cur.length && !fits([...cur, word])) emit(cur.length);
				} else emit(cur.length);
			}
		}
		cur.push(word);
		if (endsSentence(word.w) && text(cur).length >= 25) emit(cur.length);
	}
	emit(cur.length);

	// No overlaps; stretch very short cues into the following gap.
	for (let i = 0; i < raw.length; i++) {
		const cue = raw[i];
		if (!cue) continue;
		const next = raw[i + 1];
		if (next && cue.end > next.start) cue.end = next.start;
		if (cue.end - cue.start < minDur) {
			cue.end = Math.min(
				cue.start + minDur,
				next ? next.start : cue.start + minDur,
			);
		}
		if (cue.end <= cue.start) cue.end = cue.start + 0.05;
		if (next && next.start < cue.end) next.start = cue.end;
	}
	return raw.map((c, i) => ({
		index: i + 1,
		...c,
		lines: wrapCaptionLines(c.text, maxChars) ?? [c.text],
	}));
}

function formatCueTime(sec: number, sep: "," | "."): string {
	const ms = Math.max(0, Math.round(sec * 1000));
	const h = Math.floor(ms / 3600000);
	const m = Math.floor((ms % 3600000) / 60000);
	const s = Math.floor((ms % 60000) / 1000);
	const r = ms % 1000;
	return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}${sep}${String(r).padStart(3, "0")}`;
}

export function captionsToSrt(cues: readonly CaptionCue[]): string {
	return cues
		.map(
			(c) =>
				`${c.index}\n${formatCueTime(c.start, ",")} --> ${formatCueTime(c.end, ",")}\n${c.lines.join("\n")}\n`,
		)
		.join("\n");
}

export function captionsToVtt(cues: readonly CaptionCue[]): string {
	return `WEBVTT\n\n${cues
		.map(
			(c) =>
				`${formatCueTime(c.start, ".")} --> ${formatCueTime(c.end, ".")}\n${c.lines.join("\n")}\n`,
		)
		.join("\n")}`;
}

/**
 * Plain transcript for YouTube's "upload without timing" (auto-sync): spoken
 * words only, a paragraph break at pauses over 1.5 s or every ~6 sentences.
 */
export function captionsToPlainText(cues: readonly CaptionCue[]): string {
	const paragraphs: string[] = [];
	let cur: string[] = [];
	let sentences = 0;
	cues.forEach((c, i) => {
		cur.push(c.text);
		const sentenceEnd = /[.?!]["”')]*$/.test(c.text);
		if (sentenceEnd) sentences++;
		const next = cues[i + 1];
		if (
			!next ||
			next.start - c.end > 1.5 ||
			(sentences >= 6 && sentenceEnd)
		) {
			paragraphs.push(cur.join(" "));
			cur = [];
			sentences = 0;
		}
	});
	return paragraphs.length ? `${paragraphs.join("\n\n")}\n` : "";
}

export interface CaptionFileValidation {
	ok: boolean;
	issues: string[];
	issueCount: number;
	cues: number;
}

const parseCueTime = (s: string) => {
	const m = s.match(/^(\d{2}):(\d{2}):(\d{2})[,.](\d{3})$/);
	return m
		? Number(m[1]) * 3600 +
				Number(m[2]) * 60 +
				Number(m[3]) +
				Number(m[4]) / 1000
		: Number.NaN;
};

/** Re-parse an SRT or VTT file and check format, order, overlaps and limits. */
export function validateCaptionFile(
	text: string,
	kind: "srt" | "vtt",
	options: {
		maxChars?: number;
		maxLines?: number;
		maxDurSec?: number;
		durationSec?: number;
	} = {},
): CaptionFileValidation {
	const maxChars = options.maxChars ?? 42;
	const maxLines = options.maxLines ?? 2;
	const maxDur = options.maxDurSec ?? 6.05;
	const durationSec = options.durationSec ?? Number.POSITIVE_INFINITY;
	const issues: string[] = [];
	let body = text.replace(/\r\n/g, "\n");
	if (kind === "vtt") {
		if (!body.startsWith("WEBVTT\n")) issues.push("missing WEBVTT header");
		body = body.replace(/^WEBVTT\n+/, "");
	}
	const blocks = body.trim() ? body.trim().split(/\n\n+/) : [];
	let prevEnd = 0;
	let n = 0;
	for (const block of blocks) {
		const lines = block.split("\n");
		let k = 0;
		if (kind === "srt") {
			if (Number(lines[0]) !== n + 1) {
				issues.push(`cue ${n + 1}: bad index "${lines[0]}"`);
			}
			k = 1;
		}
		const tm = lines[k]?.match(/^(\S+) --> (\S+)$/);
		if (!tm) {
			issues.push(`cue ${n + 1}: bad timing line`);
			n++;
			continue;
		}
		const a = parseCueTime(tm[1] ?? "");
		const z = parseCueTime(tm[2] ?? "");
		const textLines = lines.slice(k + 1);
		if (Number.isNaN(a) || Number.isNaN(z)) {
			issues.push(`cue ${n + 1}: bad timestamp format`);
		}
		if (z <= a) issues.push(`cue ${n + 1}: end not after start`);
		if (a < prevEnd - 1e-6)
			issues.push(`cue ${n + 1}: overlaps the previous cue`);
		if (z > durationSec + 0.5)
			issues.push(`cue ${n + 1}: ends after the video`);
		if (textLines.length === 0 || textLines.some((l) => !l.trim())) {
			issues.push(`cue ${n + 1}: empty text line`);
		}
		if (textLines.length > maxLines) {
			issues.push(`cue ${n + 1}: ${textLines.length} lines`);
		}
		for (const l of textLines) {
			if (l.length > maxChars) {
				issues.push(`cue ${n + 1}: line of ${l.length} chars`);
			}
		}
		if (z - a > maxDur)
			issues.push(`cue ${n + 1}: ${(z - a).toFixed(2)} s long`);
		prevEnd = z;
		n++;
	}
	return {
		ok: issues.length === 0,
		issues: issues.slice(0, 20),
		issueCount: issues.length,
		cues: n,
	};
}

/** All three caption files from one cue list. */
export function buildCaptionFiles(words: readonly TimedWord[]): {
	srt: string;
	vtt: string;
	txt: string;
	cues: number;
} {
	const cues = packCaptionCues(words);
	return {
		srt: captionsToSrt(cues),
		vtt: captionsToVtt(cues),
		txt: captionsToPlainText(cues),
		cues: cues.length,
	};
}

/** Longest file-name stem for the downloads. */
export const DOWNLOAD_STEM_MAX = 60;

/**
 * A safe, readable file-name stem from the run name: lowercase words joined
 * by hyphens, at most `DOWNLOAD_STEM_MAX` characters, cut at a word boundary
 * when there is one (never mid-word with a trailing hyphen).
 */
export function downloadFileStem(name: string): string {
	const slug = name
		.toLowerCase()
		.replace(/\.[a-z0-9]{2,4}$/, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
	if (slug.length <= DOWNLOAD_STEM_MAX) return slug || "video";
	const cut = slug.slice(0, DOWNLOAD_STEM_MAX + 1);
	const boundary = cut.lastIndexOf("-");
	// A boundary in the first half would throw away too much; cut hard then.
	const stem =
		boundary >= DOWNLOAD_STEM_MAX / 2
			? cut.slice(0, boundary)
			: slug.slice(0, DOWNLOAD_STEM_MAX);
	return stem.replace(/-+$/g, "") || "video";
}
